import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Stack,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useNavigate } from "react-router-dom";
import "./outdoor.css";
import * as XLSX from "xlsx";

const severityMapping = {
  0: "All OK",
  1: "Minor",
  2: "Major",
  3: "Critical",
};

const fanStatus = {
  0: "Off",
  1: "On",
  2: "Fail",
};

const headerFieldMapping = {
  "Site ID": "site_id",
  "Date/Time": "controller_datetime",
  "Frame Type": "frame_type",
  "Severity Status": "severity_status",
  "Smoke Alarm": "smoke_alarm",
  "Door Alarm": "door_alarm",
  "HTC Alarm": "htc_alarm",
  "USys Low Alarm": "usys_low_alam",
  "TCAN Fail Alarm": "tcan_fail_alarm",
  "TAMB Fail Alarm": "tamb_fail_alarm",
  "Water Leakage Alarm": "water_leakage_alarm",
  "Water Logging Alarm": "water_logging_alarm",
  "Humidity Fail": "humidity_fail",
  "Fan 01 Status": "fan01_status",
  "Fan 02 Status": "fan02_status",
  "Fan 03 Status": "fan03_status",
  "Fan 04 Status": "fan04_status",
  "Fan 05 Status": "fan05_status",
  "Fan 06 Status": "fan06_status",
  "Unauthorised Access": "unauthorised_access",
  "Temperature Outside(°C)": "temperature_outside",
  "Temperature Inside(°C)": "temperature_inside",
  "Humidity(%)": "humidity",
  "Lock Battery Voltage": "system_voltage",
  "Fan Run Hour 01": "fan_run_hour_01",
  "Fan Run Hour 02": "fan_run_hour_02",
  "Fan Run Hour 03": "fan_run_hour_03",
  "Fan Run Hour 04": "fan_run_hour_04",
  "Fan Run Hour 05": "fan_run_hour_05",
  "Fan Run Hour 06": "fan_run_hour_06",
  "Circle ": "state",
};

const rowsPerPage = 25; // Define rows per page
const apiUrl =
  "https://api.bharatnet.sunshineiotronics.com/api/v2/historical_data/";

const OutDoorCabinet = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dropdownValue, setDropdownValue] = useState("");
  const [initialData, setInitialData] = useState([]);
  const [paginationEnabled, setPaginationEnabled] = useState(true);
  const stateName = sessionStorage.getItem("selectedState");
  const siteId = sessionStorage.getItem("selectedSiteId");
  const navigate = useNavigate();

  useEffect(() => {
    const userData = sessionStorage.getItem("userData");
    if (!userData) {
      navigate("/");
    } else {
      sessionStorage.setItem("lastVisitedPage", "/historical_data");
    }
  }, [navigate]);

  useEffect(() => {
    fetchInitialData();
  }, [currentPage]);

  const fetchInitialData = async () => {
    setIsLoading(true);
    const requestData = {
      site_id: siteId,
      page: currentPage - 1,
      limit: rowsPerPage,
    };

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }

      const responseData = await response.json();
      setData(responseData);
      setInitialData(responseData);
      setPaginationEnabled(true);
    } catch (error) {
      console.error("Error fetching data:", error);
      setPaginationEnabled(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    // Function to format date to dd-mm-yyyy
    const formatDate = (date) => {
      const d = new Date(date);
      const day = d.getDate().toString().padStart(2, "0");
      const month = (d.getMonth() + 1).toString().padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const formattedFromDate = formatDate(fromDate);
    const formattedToDate = formatDate(toDate);

    const requestData = {
      site_id: siteId,
      severity_status: dropdownValue,
      from_date: formattedFromDate,
      to_date: formattedToDate,
    };
    console.log(requestData);
    try {
      const response = await fetch(
        "https://api.bharatnet.sunshineiotronics.com/api/v2/historical/filter",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch filtered data");
      }

      const responseData = await response.json();
      setData(responseData);
      setFilteredData(responseData);
      setPaginationEnabled(false);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
      setData(initialData);
      setPaginationEnabled(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };
  const handleDownloadExcel = () => {
    if (filteredData.length === 0) return;

    // Map filtered data to ensure all blank spaces are replaced with "0"
    const formattedData = filteredData.map((record) => {
      const newRecord = {};
      Object.keys(record).forEach((key) => {
        newRecord[key] =
          record[key] === null || record[key] === "" ? "0" : record[key];
      });
      return newRecord;
    });

    // Include the header field mapping for proper ordering and labels
    const tableData = [Object.keys(headerFieldMapping)]; // Header row
    formattedData.forEach((row) => {
      const formattedRow = [];
      Object.keys(headerFieldMapping).forEach((header) => {
        const field = headerFieldMapping[header];
        formattedRow.push(row[field] !== undefined ? row[field] : "0");
      });
      tableData.push(formattedRow);
    });

    // Create a worksheet with the formatted table data
    const worksheet = XLSX.utils.aoa_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Filtered Data");

    // Export the workbook to an Excel file
    const excelFile = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelFile], { type: "application/octet-stream" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "filtered_data.xlsx";
    link.click();
  };

  return (
    <Box m="1.5rem 2.5rem">
      <div className="container-fluid page-body-wrapper">
        <br />
        <br />
        <div className="main-panel">
          <div className="content-wrapper">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack spacing={2}>
                <Breadcrumbs
                  separator={<NavigateNextIcon fontSize="medium" />}
                  aria-label="breadcrumb"
                >
                  <Link key="1" sx={{ color: "black" }} href="/dashboard">
                    Dashboard
                  </Link>
                  <Link key="2" sx={{ color: "black" }} href={`/dashboard`}>
                    {stateName}
                  </Link>
                  <Link
                    key="3"
                    sx={{ color: "black" }}
                    href={`/${stateName}/displaySite`}
                  >
                    Display Sites
                  </Link>
                  <Link
                    key="3"
                    sx={{ color: "black" }}
                    href={`/historical_data`}
                  >
                    Historical Data
                  </Link>
                  <Typography key="4" sx={{ color: "text.primary" }}>
                    Smart Cabinet
                  </Typography>
                </Breadcrumbs>
              </Stack>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                {siteId}
              </Typography>
            </Box>
            <br />
            <br />
          </div>
        </div>
      </div>

      {/* Form Section */}
      <form
        className="form-control"
        onSubmit={handleFormSubmit}
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "1rem",
          width: "900px",
          height: "auto",
          padding: "1rem",
          border: "1px solid #ccc",
          borderRadius: "8px",
        }}
      >
        <TextField
          label="From Date"
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          style={{ width: "10rem" }}
        />
        <TextField
          label="To Date"
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          style={{ width: "10rem" }}
        />
        <FormControl>
          <InputLabel id="dropdown-label">Select Severity</InputLabel>
          <Select
            labelId="dropdown-label"
            value={dropdownValue}
            onChange={(e) => setDropdownValue(e.target.value)}
            style={{ width: "10rem" }}
          >
            <MenuItem value="0">All OK</MenuItem>
            <MenuItem value="1">Minor</MenuItem>
            <MenuItem value="2">Major</MenuItem>
            <MenuItem value="3">Critical</MenuItem>
          </Select>
        </FormControl>
        <Button
          type="submit"
          className="submit-button"
          variant="outlined"
          color="primary"
        >
          Filter
        </Button>
        <Button
          class="cssbuttons-io-button"
          variant="outlined"
          onClick={handleDownloadExcel}
          className="submit-button"
          style={{ display: filteredData.length > 0 ? "inline-block" : "none" }}
        >
          Download
        </Button>
      </form>

      {/* Table Section */}
      <Box mt={3}>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <TableContainer component={Paper}>
            <Table size="medium" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f4f4f4" }}>
                  {Object.keys(headerFieldMapping).map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        fontWeight: "bold",
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        width: header === "Date/Time" ? "250px" : "auto",
                        whiteSpace:
                          header === "Date/Time" ? "nowrap" : "normal",
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data.map((record, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:nth-of-type(even)": { backgroundColor: "#f9f9f9" },
                      }}
                    >
                      {Object.keys(headerFieldMapping).map((header) => {
                        const field = headerFieldMapping[header];

                        return (
                          <TableCell
                            key={field}
                            sx={{
                              border: "1px solid #ddd",
                              padding: "8px",
                              textAlign: "center",
                              width: header === "Date/Time" ? "250px" : "auto",
                              whiteSpace:
                                header === "Date/Time" ? "nowrap" : "normal",
                            }}
                          >
                            {field === "severity_status"
                              ? severityMapping[record[field]] || "Unknown"
                              : field === "fan01_status" ||
                                  field === "fan02_status" ||
                                  field === "fan03_status" ||
                                  field === "fan04_status" ||
                                  field === "fan05_status" ||
                                  field === "fan06_status"
                                ? fanStatus[record[field]] || "Unknown"
                                : field === "system_voltage"
                                  ? parseFloat(record[field]).toFixed(2)
                                  : field === "frame_type"
                                    ? String(record[field] ?? "").charAt(0)
                                    : field === "sw_version"
                                      ? String(record[field] ?? "").substring(
                                          0,
                                          3,
                                        )
                                      : record[field]}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={Object.keys(headerFieldMapping).length}
                      align="center"
                      sx={{ border: "1px solid #ddd", padding: "8px" }}
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {paginationEnabled && (
        <Box mt={3} display="flex" justifyContent="start">
          <Stack direction="row" spacing={2}>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              style={{
                padding: "8px 16px",
                backgroundColor: currentPage === 1 ? "#ddd" : "#997d3d",
                color: currentPage === 1 ? "#aaa" : "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
              }}
            >
              Previous
            </button>
            <button
              disabled={data.length < rowsPerPage}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              style={{
                padding: "8px 16px",
                backgroundColor: data.length < rowsPerPage ? "#ddd" : "#997d3d",
                color: data.length < rowsPerPage ? "#aaa" : "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: data.length < rowsPerPage ? "not-allowed" : "pointer",
              }}
            >
              Next
            </button>
          </Stack>
        </Box>
      )}
      <br />
    </Box>
  );
};

export default OutDoorCabinet;
